/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import type { PaginationInfo } from "@/lib/utils/responses";

export interface PaginationNormalizationInput {
  rawPagination?: PaginationInfo | null;
  fallbackPage: number;
  fallbackLimit?: number;
  itemsLength: number;
  totalOverride?: number;
  allowNullPagination?: boolean;
  clampTotalToZero?: boolean;
}

export function buildFallbackPagination(
  page: number,
  limit: number,
  totalItems: number,
  options?: { allowZeroPages?: boolean; normalizePage?: boolean },
): PaginationInfo {
  const total = Math.max(totalItems, 0);
  const safeLimit = Math.max(limit, 1);
  const pages =
    total === 0 && options?.allowZeroPages
      ? 0
      : Math.max(1, Math.ceil(total / safeLimit));
  const safePage = options?.normalizePage ? Math.max(1, page) : page;

  return {
    page: safePage,
    limit: safeLimit,
    total,
    pages,
    hasMore: pages > 0 && safePage < pages,
    hasPrevious: safePage > 1,
  };
}

export function normalizePaginationResult(
  input: PaginationNormalizationInput,
): { pagination: PaginationInfo | null; total: number; pageCount: number } {
  const {
    rawPagination,
    fallbackPage,
    fallbackLimit,
    itemsLength,
    totalOverride,
    allowNullPagination,
    clampTotalToZero,
  } = input;

  const totalFromPagination =
    typeof rawPagination?.total === "number" &&
    Number.isFinite(rawPagination.total)
      ? rawPagination.total
      : undefined;
  const resolvedTotal = totalFromPagination ?? totalOverride ?? itemsLength;
  const total = clampTotalToZero ? Math.max(0, resolvedTotal) : resolvedTotal;

  const limitFromPagination =
    typeof rawPagination?.limit === "number" &&
    Number.isFinite(rawPagination.limit) &&
    rawPagination.limit > 0
      ? rawPagination.limit
      : undefined;
  const normalizedLimit =
    limitFromPagination ?? fallbackLimit ?? (itemsLength > 0 ? itemsLength : 1);

  const pagesFromPagination =
    typeof rawPagination?.pages === "number" &&
    Number.isFinite(rawPagination.pages) &&
    rawPagination.pages >= 0
      ? rawPagination.pages
      : undefined;
  const computedPageCount =
    pagesFromPagination !== undefined
      ? Math.max(0, pagesFromPagination)
      : total === 0
        ? 0
        : Math.max(1, Math.ceil(total / normalizedLimit));

  const pageFromPagination =
    typeof rawPagination?.page === "number" &&
    Number.isFinite(rawPagination.page) &&
    rawPagination.page > 0
      ? rawPagination.page
      : fallbackPage;

  const hasMore =
    typeof rawPagination?.hasMore === "boolean"
      ? rawPagination.hasMore
      : computedPageCount > 0 && pageFromPagination < computedPageCount;

  const hasPrevious =
    typeof rawPagination?.hasPrevious === "boolean"
      ? rawPagination.hasPrevious
      : pageFromPagination > 1;

  const pagination =
    rawPagination || !allowNullPagination
      ? {
          page: pageFromPagination,
          limit: normalizedLimit,
          total,
          pages: computedPageCount,
          hasMore,
          hasPrevious,
        }
      : null;

  return {
    pagination,
    total,
    pageCount: pagination?.pages ?? computedPageCount,
  };
}
